import React, { useEffect } from 'react';
import { CheckCircle2, FileText, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type AIProcessingPhase = 'uploading' | 'extracting' | 'complete';

interface AIDocumentScanLoaderProps {
  phase: AIProcessingPhase;
  previewUrl?: string;
  previewMime?: string;
  onDone?: () => void;
}

const COMPLETE_FLASH_MS = 600;

/**
 * Compact inline document status — lives inside the upload dropzone so it
 * stays visible on mobile without scrolling. Intentionally calm and static
 * (no spinner, moving bar, or cycling text) so the upload screen is easy on
 * the eye. The `phase` -> `onDone` timing is preserved so the OCR success /
 * failed feedback still resolves once processing completes.
 */
const AIDocumentScanLoader: React.FC<AIDocumentScanLoaderProps> = ({
  phase,
  previewUrl,
  previewMime,
  onDone,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (phase !== 'complete') return;
    const id = window.setTimeout(() => onDone?.(), COMPLETE_FLASH_MS);
    return () => window.clearTimeout(id);
  }, [phase, onDone]);

  const statusKey =
    phase === 'uploading'
      ? 'documents.aiLoader.uploading'
      : phase === 'extracting'
        ? 'documents.aiLoader.extracting'
        : 'documents.aiLoader.ready';

  const isImage = Boolean(previewUrl) && !!previewMime?.startsWith('image/');
  const isComplete = phase === 'complete';

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex w-full items-center gap-3 sm:gap-4"
    >
      <div className="h-16 w-14 shrink-0 overflow-hidden rounded-xl border border-[#bf0035]/10 bg-white sm:h-20 sm:w-16">
        {isImage ? (
          <img src={previewUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#bf0035]/[0.04] text-[#bf0035]">
            <FileText size={22} />
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold text-gray-900 sm:text-sm">
          {isComplete ? (
            <CheckCircle2 size={15} className="shrink-0 text-emerald-600" aria-hidden />
          ) : (
            <Sparkles size={14} className="shrink-0 text-[#bf0035]" aria-hidden />
          )}
          {t('documents.aiLoader.title')}
        </span>

        <p className="text-[11px] leading-snug text-gray-600 sm:text-xs">
          {t(statusKey)}
        </p>
      </div>
    </div>
  );
};

export default AIDocumentScanLoader;
